export type Auth0SessionLogoutOptions = {
    callback?: () => void;
    skipRedirect?: boolean;
    redirectUrl?: string;
};

export type Auth0SessionLogoutHandler = (options?: Auth0SessionLogoutOptions) => Promise<void>;

export type Auth0SessionRevalidator = () => Promise<boolean>;

let registeredLogoutHandler: Auth0SessionLogoutHandler | null = null;
let registeredSessionRevalidator: Auth0SessionRevalidator | null = null;

/**
 * Registers the browser-only Auth0 session-termination handler supplied by the React layer.
 * Must be called from a component mounted under `Auth0Provider`.
 */
export function registerAuth0LogoutHandler(handler: Auth0SessionLogoutHandler): void {
    registeredLogoutHandler = handler;
}

/** Clears the registered logout handler when Auth0 React context unmounts. */
export function unregisterAuth0LogoutHandler(): void {
    registeredLogoutHandler = null;
}

/**
 * Registers the browser-only Auth0 session revalidator supplied by the React layer.
 * `evaluateSession` uses this before mutations and in-tab navigation so remote
 * refresh-token revocation is detected before the requested action continues.
 */
export function registerAuth0SessionRevalidator(revalidator: Auth0SessionRevalidator): void {
    registeredSessionRevalidator = revalidator;
}

/** Clears the registered session revalidator when Auth0 React context unmounts. */
export function unregisterAuth0SessionRevalidator(): void {
    registeredSessionRevalidator = null;
}

/**
 * Terminates an Auth0-owned session when the shared session layer detects expiry
 * (e.g. inactivity timeout). Returns true when a handler ran.
 */
export async function invokeAuth0LogoutHandler(options?: Auth0SessionLogoutOptions): Promise<boolean> {
    if (!registeredLogoutHandler) {
        return false;
    }

    await registeredLogoutHandler(options);
    return true;
}

/**
 * Asks Auth0 whether the current refresh token is still valid. Returns true when
 * no revalidator is registered (guest, Janrain, or Auth0 provider not mounted).
 */
export async function invokeAuth0SessionRevalidator(): Promise<boolean> {
    if (!registeredSessionRevalidator) {
        return true;
    }

    return registeredSessionRevalidator();
}
